import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Tilt from "react-parallax-tilt";
import { motion, useReducedMotion } from "framer-motion";
import SplashCursor from "./ui/SplashCursor";
import GlassPanel from "./ui/GlassPanel";
import MagneticButton from "./ui/MagneticButton";
import Scene3D from "./ui/Scene3D";

// Real figures reused from aboutus.js / marketing-statistics.js — no fabricated stats.
const trustStats = [
  { value: "5+", label: "Happy Clients" },
  { value: "95%", label: "Client Satisfaction" },
  { value: "300%", label: "Avg. ROI" },
];

const floatingCards = [
  { label: "SEO Rankings", value: "Top 3", top: "6%", left: "-6%", delay: 0 },
  { label: "Avg. ROI", value: "300%", top: "62%", left: "-4%", delay: 0.6 },
  { label: "Client Satisfaction", value: "95%", top: "34%", left: "78%", delay: 1.1 },
];

export default function Hero() {
  const navigate = useNavigate();
  const sceneRef = useRef(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const prefersReducedMotion = useReducedMotion();

  const handleSceneMouseMove = (e) => {
    if (prefersReducedMotion || !sceneRef.current) return;
    const rect = sceneRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const relY = (e.clientY - rect.top - rect.height / 2) / rect.height;
    setParallax({ x: relX * 18, y: relY * 18 });
  };

  const handleSceneMouseLeave = () => setParallax({ x: 0, y: 0 });

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>SLGT Digital - Best Digital Marketing in Anakapalle & Visakhapatnam</title>
        <meta name="description" content="SLGT Digital helps brands grow online with expert SEO, SMM, Web Design, Google Ads, and PR strategies. Request your quote today!" />
        <meta name="keywords" content="SLGT Digital Marketing, SEO Anakapalle, Digital Marketing Vizag, Website Design, Google Ads Agency, Social Media Marketing" />
        <link rel="canonical" href="https://www.slgt.in" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SLGT",
            "url": "https://www.slgt.in",
            "logo": "https://www.slgt.in/assets/slgt-logo.png",
            "sameAs": [
              "https://www.instagram.com/slgt_digital_marketing_agency/",
              "https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/",
              "https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8500501823",
              "contactType": "Customer Support"
            }
          }
        `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <Box
        id="home-section"
        sx={{
          display: "flex",
          flexDirection: { xs: "column-reverse", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 3, sm: 4, md: 6 },
          pt: { xs: 14, md: 16 },
          gap: { xs: 6, md: 4 },
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          bgcolor: "#05050A",
          fontFamily: "Nunito, sans-serif",
        }}
      >
        <SplashCursor RAINBOW_MODE={false} COLOR="#FD8112" />

        {/* Left Section */}
        <Box
          sx={{
            flex: 1,
            pr: { md: 4 },
            textAlign: { xs: "center", md: "left" },
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 800,
                color: "#fff",
                mb: 2,
                lineHeight: 1.15,
                fontSize: { xs: "2.25rem", sm: "3rem", md: "3.5rem" },
              }}
            >
              Your Voice,
              <br />
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #FD8112, #FF5F6D)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                }}
              >
                Your Brand
              </Box>{" "}
              Elevated
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{
                color: "rgba(255,255,255,0.75)",
                mb: 4,
                maxWidth: 520,
                mx: { xs: "auto", md: 0 },
                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontWeight: 500,
              }}
            >
              We help businesses attract more customers, generate quality leads,
              and grow online with smart digital marketing strategies.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
                mb: 5,
              }}
            >
              <MagneticButton
                variant="contained"
                onClick={() => navigate("/requestquotes")}
                sx={{
                  background: "linear-gradient(135deg, #FD8112, #FF5F6D)",
                  color: "#fff",
                  py: 1.5,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Get Free Strategy Call
              </MagneticButton>
              <MagneticButton
                variant="outlined"
                onClick={() => navigate("/services")}
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.35)",
                  py: 1.5,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "#FD8112",
                    background: "rgba(255,255,255,0.06)",
                    boxShadow: "none",
                  },
                }}
              >
                View Our Work
              </MagneticButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 3, sm: 5 },
                justifyContent: { xs: "center", md: "flex-start" },
                flexWrap: "wrap",
              }}
            >
              {trustStats.map((stat) => (
                <Box key={stat.label} sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography sx={{ color: "#FD8112", fontWeight: 800, fontSize: "1.5rem" }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Box>

        {/* Right Section — floating "dashboard" scene */}
        <Box
          ref={sceneRef}
          onMouseMove={handleSceneMouseMove}
          onMouseLeave={handleSceneMouseLeave}
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pt: { xs: 4, md: 0 },
            zIndex: 1,
            minHeight: { xs: 340, md: 500 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: { xs: "-10% -5%", md: "-15%" },
              zIndex: 0,
              opacity: { xs: 0.7, md: 1 },
            }}
          >
            <Scene3D variant="hero" />
          </Box>

          <motion.div
            animate={{ x: parallax.x, y: parallax.y }}
            transition={{ type: "spring", stiffness: 80, damping: 20 }}
            style={{ width: "100%", maxWidth: 460, position: "relative", zIndex: 1 }}
          >
            <Tilt
              tiltMaxAngleX={14}
              tiltMaxAngleY={14}
              perspective={1000}
              scale={1.03}
              transitionSpeed={400}
              glareEnable={true}
              glareMaxOpacity={0.15}
              style={{ width: "100%" }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/slgt-logo.png`}
                alt="SLGT Digital Marketing Agency Logo"
                loading="lazy"
                sx={{
                  width: "100%",
                  borderRadius: "20px",
                  boxShadow: "0 30px 60px rgba(0,0,0,0.45)",
                }}
              />
            </Tilt>
          </motion.div>

          {floatingCards.map((card) => (
            <motion.div
              key={card.label}
              style={{ position: "absolute", top: card.top, left: card.left, zIndex: 1 }}
              animate={
                prefersReducedMotion
                  ? undefined
                  : { y: [0, -12, 0], x: parallax.x * -0.6, }
              }
              transition={{
                y: { duration: 5 + card.delay, repeat: Infinity, ease: "easeInOut" },
                x: { type: "spring", stiffness: 80, damping: 20 },
              }}
            >
              <GlassPanel
                tone="dark"
                sx={{ px: 2.5, py: 1.5, display: { xs: "none", sm: "block" } }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
                  {card.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem" }}>
                  {card.label}
                </Typography>
              </GlassPanel>
            </motion.div>
          ))}
        </Box>
      </Box>
    </>
  );
}
